/**
 * Webhook Form Component
 * Create or edit webhook configurations
 */

import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';

interface Webhook {
  id?: number;
  name: string;
  webhookUrl: string;
  webhookType: string;
  eventType: string;
  isEnabled: boolean;
  mentionRole: boolean;
  roleId?: string;
  customMessage?: string;
  colorOverride?: string;
}

interface WebhookFormProps {
  webhook?: Webhook | null;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const WebhookForm: React.FC<WebhookFormProps> = ({
  webhook,
  onSuccess,
  onCancel,
}) => {
  const isEditing = !!webhook?.id;

  const [formData, setFormData] = useState<Webhook>({
    name: '',
    webhookUrl: '',
    webhookType: 'discord',
    eventType: 'payment.received',
    isEnabled: true,
    mentionRole: false,
    roleId: '',
    customMessage: '',
    colorOverride: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>(
    {}
  );

  const eventTypeCategories = {
    Payment: [
      { value: 'payment.received', label: 'Payment Received', emoji: '💳' },
      { value: 'payment.failed', label: 'Payment Failed', emoji: '❌' },
      { value: 'payment.refunded', label: 'Payment Refunded', emoji: '↩️' },
    ],
    Invoice: [
      { value: 'invoice.created', label: 'Invoice Created', emoji: '📄' },
      { value: 'invoice.paid', label: 'Invoice Paid', emoji: '✅' },
      { value: 'invoice.overdue', label: 'Invoice Overdue', emoji: '⚠️' },
    ],
    Order: [
      { value: 'order.placed', label: 'Order Placed', emoji: '🛒' },
      { value: 'order.fraud', label: 'Order Fraud', emoji: '🚨' },
      { value: 'order.cancelled', label: 'Order Cancelled', emoji: '❌' },
    ],
    Service: [
      { value: 'service.created', label: 'Service Created', emoji: '🆕' },
      { value: 'service.activated', label: 'Service Activated', emoji: '✅' },
      { value: 'service.suspended', label: 'Service Suspended', emoji: '⏸️' },
      { value: 'service.terminated', label: 'Service Terminated', emoji: '🛑' },
    ],
    Ticket: [
      { value: 'ticket.created', label: 'Ticket Created', emoji: '🎫' },
      { value: 'ticket.replied', label: 'Ticket Replied', emoji: '💬' },
    ],
    Domain: [
      { value: 'domain.registered', label: 'Domain Registered', emoji: '🌐' },
      { value: 'domain.transferred', label: 'Domain Transferred', emoji: '🔄' },
      { value: 'domain.renewed', label: 'Domain Renewed', emoji: '🔄' },
    ],
    Affiliate: [
      { value: 'affiliate.signup', label: 'Affiliate Signup', emoji: '🤝' },
      { value: 'affiliate.commission', label: 'Affiliate Commission', emoji: '💰' },
    ],
    Client: [
      { value: 'client.registered', label: 'Client Registered', emoji: '👤' },
      { value: 'client.login_failed', label: 'Client Login Failed', emoji: '🔒' },
    ],
  };

  const predefinedColors = [
    { name: 'Green (Success)', value: '#00FF00' },
    { name: 'Red (Error)', value: '#FF0000' },
    { name: 'Blue (Info)', value: '#0099FF' },
    { name: 'Orange (Warning)', value: '#FFA500' },
    { name: 'Purple (Order)', value: '#9B59B6' },
    { name: 'Yellow (Service)', value: '#FFD700' },
    { name: 'Pink (Affiliate)', value: '#FF69B4' },
    { name: 'Cyan (Client)', value: '#00FFFF' },
  ];

  useEffect(() => {
    if (webhook) {
      setFormData({
        ...webhook,
        roleId: webhook.roleId || '',
        customMessage: webhook.customMessage || '',
        colorOverride: webhook.colorOverride || '',
      });
    }
  }, [webhook]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.webhookUrl.trim()) {
      errors.webhookUrl = 'Webhook URL is required';
    } else if (!formData.webhookUrl.startsWith('https://')) {
      errors.webhookUrl = 'Webhook URL must start with https://';
    }

    if (formData.mentionRole && !formData.roleId?.trim()) {
      errors.roleId = 'Role ID is required when mention is enabled';
    }

    if (formData.colorOverride && !/^#[0-9A-F]{6}$/i.test(formData.colorOverride)) {
      errors.colorOverride = 'Color must be a valid hex code (e.g., #FF0000)';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const payload = {
        ...formData,
        roleId: formData.roleId || null,
        customMessage: formData.customMessage || null,
        colorOverride: formData.colorOverride || null,
      };

      if (isEditing && webhook?.id) {
        await apiClient.put(`/api/v1/admin/webhooks/${webhook.id}`, payload);
      } else {
        await apiClient.post('/admin/webhooks', payload);
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save webhook');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Basic Information */}
      <div className="bg-slate-800/80 rounded-lg p-6 border border-slate-700/50">
        <h3 className="text-lg font-semibold text-white mb-4">
          Basic Information
        </h3>

        <div className="space-y-4">
          {/* Name */}
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-slate-300 mb-2"
            >
              Webhook Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full bg-slate-700/50 border ${
                validationErrors.name ? 'border-red-500' : 'border-gray-600'
              } text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500`}
              placeholder="e.g., Payment Notifications"
            />
            {validationErrors.name && (
              <p className="text-red-400 text-sm mt-1">{validationErrors.name}</p>
            )}
          </div>

          {/* Webhook URL */}
          <div>
            <label
              htmlFor="webhookUrl"
              className="block text-sm font-medium text-slate-300 mb-2"
            >
              Webhook URL *
            </label>
            <input
              type="url"
              id="webhookUrl"
              name="webhookUrl"
              value={formData.webhookUrl}
              onChange={handleChange}
              className={`w-full bg-slate-700/50 border ${
                validationErrors.webhookUrl ? 'border-red-500' : 'border-gray-600'
              } text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500`}
              placeholder="https://discord.com/api/webhooks/..."
            />
            {validationErrors.webhookUrl && (
              <p className="text-red-400 text-sm mt-1">
                {validationErrors.webhookUrl}
              </p>
            )}
            <p className="text-slate-400 text-sm mt-1">
              Discord webhook URL from Server Settings → Integrations → Webhooks
            </p>
          </div>

          {/* Webhook Type */}
          <div>
            <label
              htmlFor="webhookType"
              className="block text-sm font-medium text-slate-300 mb-2"
            >
              Webhook Type
            </label>
            <select
              id="webhookType"
              name="webhookType"
              value={formData.webhookType}
              onChange={handleChange}
              className="w-full bg-slate-700/50 border border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="discord">Discord</option>
              <option value="slack">Slack (Coming Soon)</option>
              <option value="custom">Custom HTTP</option>
            </select>
          </div>

          {/* Event Type */}
          <div>
            <label
              htmlFor="eventType"
              className="block text-sm font-medium text-slate-300 mb-2"
            >
              Event Type *
            </label>
            <select
              id="eventType"
              name="eventType"
              value={formData.eventType}
              onChange={handleChange}
              className="w-full bg-slate-700/50 border border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              {Object.entries(eventTypeCategories).map(([category, events]) => (
                <optgroup key={category} label={category}>
                  {events.map((event) => (
                    <option key={event.value} value={event.value}>
                      {event.emoji} {event.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Enabled */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isEnabled"
              name="isEnabled"
              checked={formData.isEnabled}
              onChange={handleChange}
              className="w-4 h-4 text-orange-600 bg-slate-700/50 border-gray-600 rounded focus:ring-orange-500"
            />
            <label htmlFor="isEnabled" className="ml-2 text-sm text-slate-300">
              Enable this webhook
            </label>
          </div>
        </div>
      </div>

      {/* Discord-Specific Settings */}
      {formData.webhookType === 'discord' && (
        <div className="bg-slate-800/80 rounded-lg p-6 border border-slate-700/50">
          <h3 className="text-lg font-semibold text-white mb-4">
            Discord Settings
          </h3>

          <div className="space-y-4">
            {/* Mention Role */}
            <div>
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  id="mentionRole"
                  name="mentionRole"
                  checked={formData.mentionRole}
                  onChange={handleChange}
                  className="w-4 h-4 text-orange-600 bg-slate-700/50 border-gray-600 rounded focus:ring-orange-500"
                />
                <label htmlFor="mentionRole" className="ml-2 text-sm text-slate-300">
                  Mention a role
                </label>
              </div>

              {formData.mentionRole && (
                <div className="ml-6">
                  <input
                    type="text"
                    id="roleId"
                    name="roleId"
                    value={formData.roleId}
                    onChange={handleChange}
                    className={`w-full bg-slate-700/50 border ${
                      validationErrors.roleId ? 'border-red-500' : 'border-gray-600'
                    } text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500`}
                    placeholder="Role ID (e.g., 123456789012345678)"
                  />
                  {validationErrors.roleId && (
                    <p className="text-red-400 text-sm mt-1">
                      {validationErrors.roleId}
                    </p>
                  )}
                  <p className="text-slate-400 text-sm mt-1">
                    Right-click role → Copy ID (requires Developer Mode enabled)
                  </p>
                </div>
              )}
            </div>

            {/* Custom Message */}
            <div>
              <label
                htmlFor="customMessage"
                className="block text-sm font-medium text-slate-300 mb-2"
              >
                Custom Message (Optional)
              </label>
              <textarea
                id="customMessage"
                name="customMessage"
                value={formData.customMessage}
                onChange={handleChange}
                rows={3}
                className="w-full bg-slate-700/50 border border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Additional message to include with webhook"
              />
              <p className="text-slate-400 text-sm mt-1">
                This message will appear above the embed
              </p>
            </div>

            {/* Color Override */}
            <div>
              <label
                htmlFor="colorOverride"
                className="block text-sm font-medium text-slate-300 mb-2"
              >
                Custom Embed Color (Optional)
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  id="colorOverride"
                  name="colorOverride"
                  value={formData.colorOverride}
                  onChange={handleChange}
                  className={`flex-1 bg-slate-700/50 border ${
                    validationErrors.colorOverride
                      ? 'border-red-500'
                      : 'border-gray-600'
                  } text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  placeholder="#FF0000"
                />
                {formData.colorOverride && (
                  <div
                    className="w-12 h-10 rounded-lg border border-gray-600"
                    style={{ backgroundColor: formData.colorOverride }}
                  />
                )}
              </div>
              {validationErrors.colorOverride && (
                <p className="text-red-400 text-sm mt-1">
                  {validationErrors.colorOverride}
                </p>
              )}

              {/* Predefined Colors */}
              <div className="flex flex-wrap gap-2 mt-3">
                {predefinedColors.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        colorOverride: color.value,
                      }))
                    }
                    className="flex items-center space-x-2 px-3 py-1 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg text-sm transition-colors"
                    title={color.name}
                  >
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: color.value }}
                    />
                    <span className="text-slate-300">{color.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Actions */}
      <div className="flex justify-end space-x-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg font-medium transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading
            ? 'Saving...'
            : isEditing
            ? 'Update Webhook'
            : 'Create Webhook'}
        </button>
      </div>
    </form>
  );
};
